import { Quad } from '@marmon/shared/types/quad';
import { Quartile } from '@marmon/shared/types/quartile';
import { RollupEntity } from '../utils';

export type QuadProfitTimePeriodGroup = `quad${Quad}` | 'quadTotal';

export type CustomerQuartileProfitTimePeriodGroup =
	`quartileCustomers${Quartile | 'Total'}`;

export type ProductQuartileProfitTimePeriodGroup =
	`quartileProducts${Quartile | 'Total'}`;

export type QuartileProfitTimePeriodGroup =
	| ProductQuartileProfitTimePeriodGroup
	| CustomerQuartileProfitTimePeriodGroup;

export type ProfitTimePeriodGroup =
	| QuadProfitTimePeriodGroup
	| QuartileProfitTimePeriodGroup;

export type QuadProfitGroup = {
	[key in QuadProfitTimePeriodGroup]: QuadPeriodProfit;
};

export type CustomerQuartileProfitGroup = {
	[key in CustomerQuartileProfitTimePeriodGroup]: QuartilePeriodProfit;
};

export type ProductQuartileProfitGroup = {
	[key in ProductQuartileProfitTimePeriodGroup]: QuartilePeriodProfit;
};

export type QuartileProfitGroup =
	| CustomerQuartileProfitGroup
	| ProductQuartileProfitGroup;

type PeriodProfitBase = {
	type: 'quad' | 'quartile';

	/**
	 * key used to help indexes the differences
	 */
	key: ProfitTimePeriodGroup;

	/**
	 * This is the data when the time periods are combined
	 */
	total: TimePeriodProfit;

	/**
	 * time periods to be viewed in the model, compared against the profitability model as whole.
	 * The difference between this and the `comparisons` array is that the calculations done inside
	 * this `model` object are compared against the `quadTotal` model, while `comparisons` are compared
	 * against the specific profit objects model
	 *
	 * @note this is only needed for the quad profitability model, not the comparisons so quartile does
	 * not need to calculate this.
	 */
	timePeriods?: [TimePeriodProfit, TimePeriodProfit] | never;

	/**
	 * comparing the periods to one another against their aggregated total in the model
	 */
	comparisons: [TimePeriodProfit, TimePeriodProfit];
};

export interface QuadPeriodProfit extends PeriodProfitBase {
	type: 'quad';
	timePeriods: [TimePeriodProfit, TimePeriodProfit];
}

export interface QuartilePeriodProfit extends PeriodProfitBase {
	type: 'quartile';
	timePeriods?: never;
}

export type PeriodProfit = QuadPeriodProfit | QuartilePeriodProfit;

/**
 * Everything is assumed to be in the specific time period being calculated. Therefor I will
 * not write out the time period in which the calculation needs to be pulled from. When calculating
 * "all" it means all in that time period, not of all time.
 *
 * @legend
 * Q = quad
 * n = number
 * M = margin (contribution or material)
 * C = cost (direct or material)
 * R = revenue
 * T = transactions
 * OI = operating income
 * OH = overhead
 * ROS = return on sales
 * P = time period
 *
 * @note that transactions in the same period can span across different quads and quartiles
 * and vice verse.
 */
export type TimePeriodProfit = {
	/**
	 * R%
	 * Qn.R$ / Q.R$
	 */
	revenuePercent: number;

	/**
	 * R$
	 */
	revenueInCents: number;

	/**
	 * C$
	 */
	costInCents: number;

	/**
	 * T
	 */
	transactionCount: number;

	/**
	 * Abbr: M$
	 * R$ - C$
	 */
	marginInCents: number;

	/**
	 * Abbr: M%
	 * M$ / R
	 */
	marginPercent: number | null;

	/**
	 * Abbr: OH%
	 * Qn.T / Q.T
	 */
	overheadPercent: number;

	/**
	 * Abbr: OH$
	 * OH% * P.OH$
	 */
	overheadInCents: number;

	/**
	 * Abbr: OI$
	 * M$ - OH$
	 */
	operatingIncomeInCents: number;

	/**
	 * Abbr: %OI
	 * Qn.OI$ / OI$
	 */
	operatingIncomePercent: number;

	/**
	 * Abbr: ROS%
	 * OI$ / R$
	 */
	returnOnSalesPercent: number;

	/**
	 * The count of unique customers in this set of data
	 *
	 * @note We do not need to keep count of the entities when calculating quartiles
	 */
	customerCount?: number;

	/**
	 * The count of unique products in this set of data
	 *
	 * @note We do not need to keep count of the entities when calculating quartiles
	 */
	productCount?: number;
};

export type PeriodKey = keyof TimePeriodProfit;

export function DefaultProfit(options?: {
	includeCount: boolean;
}): TimePeriodProfit {
	const { includeCount } = { includeCount: true, ...options };
	return {
		revenuePercent: 0,
		returnOnSalesPercent: 0,
		revenueInCents: 0,
		costInCents: 0,
		marginInCents: 0,
		marginPercent: 0,
		operatingIncomeInCents: 0,
		operatingIncomePercent: 0,
		overheadInCents: 0,
		overheadPercent: 0,
		transactionCount: 0,
		...(includeCount ? { customerCount: 0, productCount: 0 } : {}),
	};
}

export function DefaultProfitGroup<T = QuartilePeriodProfit | QuadPeriodProfit>(
	type: 'quad' | 'quartile',
	key: ProfitTimePeriodGroup,
): T {
	if (type === 'quad') {
		return {
			type,
			key,
			total: DefaultProfit(),
			comparisons: [DefaultProfit(), DefaultProfit()],
			timePeriods: [DefaultProfit(), DefaultProfit()],
		} as T;
	}

	return {
		type,
		key,
		comparisons: [
			DefaultProfit({ includeCount: false }),
			DefaultProfit({ includeCount: false }),
		],
		total: DefaultProfit({ includeCount: false }),
	} as T;
}

export const DefaultQuadProfit = (): QuadProfitGroup => ({
	quad1: DefaultProfitGroup('quad', 'quad1'),
	quad2: DefaultProfitGroup('quad', 'quad2'),
	quad3: DefaultProfitGroup('quad', 'quad3'),
	quad4: DefaultProfitGroup('quad', 'quad4'),
	quadTotal: DefaultProfitGroup('quad', 'quadTotal'),
});

export const DefaultCustomerQuartileProfit =
	(): CustomerQuartileProfitGroup => ({
		quartileCustomers1: DefaultProfitGroup(
			'quartile',
			`quartileCustomers1`,
		),
		quartileCustomers2: DefaultProfitGroup(
			'quartile',
			`quartileCustomers2`,
		),
		quartileCustomers3: DefaultProfitGroup(
			'quartile',
			`quartileCustomers3`,
		),
		quartileCustomers4: DefaultProfitGroup(
			'quartile',
			`quartileCustomers4`,
		),
		quartileCustomersTotal: DefaultProfitGroup(
			'quartile',
			`quartileCustomersTotal`,
		),
	});

export const DefaultProductQuartileProfit = (): ProductQuartileProfitGroup => ({
	quartileProducts1: DefaultProfitGroup('quartile', 'quartileProducts1'),
	quartileProducts2: DefaultProfitGroup('quartile', 'quartileProducts2'),
	quartileProducts3: DefaultProfitGroup('quartile', 'quartileProducts3'),
	quartileProducts4: DefaultProfitGroup('quartile', 'quartileProducts4'),
	quartileProductsTotal: DefaultProfitGroup(
		'quartile',
		'quartileProductsTotal',
	),
});

export function getQuartileProfitKey<T = ProfitTimePeriodGroup>(
	entity: RollupEntity,
	quartile: number | 'Total',
): T {
	if (entity === 'customer') return `quartileCustomers${quartile}` as T;
	if (entity === 'product') return `quartileProducts${quartile}` as T;
	throw Error(`Entity not supported: ${entity}`);
}
